import React from 'react';

import Panel from '../../design-system/Panel';
import Typography from '../../design-system/Typography';
import Button from '../../design-system/Button';
import { colors, spacing } from '../../design-system/tokens';
import OrderSummaryCard from './OrderSummaryCard';
import type { LocationSummary } from './OrderReviewSection';

/**
 * Resumo geral do pedido — tela final do fluxo de abastecimento.
 *
 * Lista todos os locais (cada um num `OrderSummaryCard` com contador "[n/m]" e
 * lápis de edição) e o botão "Confirmar e concluir". O lápis volta para a base
 * daquele local (refinado no fluxo). Composite de feature.
 */
export interface OrderGeneralSummaryProps {
  /** Locais do pedido. */
  locations: LocationSummary[];
  /** Disparado ao clicar no lápis de um local (volta para editar a base). */
  onEditLocation?: (id: string) => void;
  /** Disparado ao confirmar e concluir o pedido. */
  onConfirm?: () => void;
  /** Desabilita "Confirmar e concluir" enquanto houver local incompleto. @default false */
  confirmDisabled?: boolean;
}

const OrderGeneralSummary = ({
  locations,
  onEditLocation,
  onConfirm,
  confirmDisabled = false,
}: OrderGeneralSummaryProps): JSX.Element => {
  const total = locations.length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
      {/* Cabeçalho */}
      <Panel padding="lg">
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}
        >
          <Typography as="h2" size="2xl" weight="bold" color="#0B29FB">
            Resumo geral do pedido
          </Typography>
          <Typography
            as="p"
            size="sm"
            weight="regular"
            color={colors.textSecondary}
          >
            Confirme os dados abaixo
          </Typography>
        </div>
      </Panel>

      {/* Card externo (normal, com shadow) envolvendo os componentes internos
          (cada um com borda e SEM shadow). */}
      <Panel padding="lg">
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}
        >
          {locations.map((loc, i) => (
            <OrderSummaryCard
              key={loc.id}
              title={loc.title}
              counter={`${i + 1}/${total}`}
              onEdit={() => onEditLocation?.(loc.id)}
              editLabel={`Editar ${loc.title}`}
              items={loc.items}
            />
          ))}
        </div>
      </Panel>

      {/* Confirmar */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="primary"
          shape="pill"
          size="xl"
          onClick={onConfirm}
          disabled={confirmDisabled}
        >
          Confirmar e concluir
        </Button>
      </div>
    </div>
  );
};

export default OrderGeneralSummary;
